import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../components/ButtonComponent";
import InputComponent from "../../components/InputComponent";
import Layout from "../../components/Layout/Layout";
import PageTitle from "../../components/Layout/PageTitle";
import useValidationSchema, { FieldConfig } from "../../components/Validations";
import Toast from "../../helpers/Toast";
import { CounterTypeDTO } from '../../models/Counter/CounterTypeDTO';
import { CounterTypeEditDTO } from '../../models/Counter/CounterTypeEditDTO';
import { CounterTypeService } from "../../services/CounterTypeService";
import { useAvailableModulesContext } from '../../context/AvailableModulesContext';

export default function CounterTypeEdit() {
    const { id } = useParams<{ id: string }>();
    const intl = useIntl();
    const navigate = useNavigate();
    const counterService = new CounterTypeService();
    const { availableModules } = useAvailableModulesContext();

    const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false);
    const [counterType, setCounterType] = useState<CounterTypeDTO>({} as CounterTypeDTO);
    const isErpCountersActive = availableModules.includes("IsActiveErpEquipmentCountersModule");

    useEffect(() => {
        const fetchCounterType = async () => {
            if (id) {
                const response = await counterService.GetById(parseInt(id));

                if (response.success) {
                    setCounterType(response.obj);
                } else {
                    Toast.Show("error", response.message);
                    navigate(-1);
                }
            }
        };

        fetchCounterType();
    }, [id]);

    const fields: FieldConfig[] = [
        ['name', 'string', 'Screens.CounterTypes.Edit.Validation.NameRequired']
    ];

    const schemaCounterTypeValidation = useValidationSchema(fields);

    const editCounterType = async () => {
        setLoadingUpdate(true);

        const editCounterTypeDTO: CounterTypeEditDTO = {
            id: parseInt(id!),
            name: counterType.name,
            description: counterType.description && counterType.description.trim() !== "" ? counterType.description : undefined,
            active: counterType.active
        };

        const { error } = schemaCounterTypeValidation.validate(editCounterTypeDTO);

        if (error) {
            Toast.Show("error", error.details[0].message);
            setLoadingUpdate(false);
            return;
        }

        const response = await counterService.Edit(editCounterTypeDTO);

        if (response.success !== true) {
            Toast.Show("error", response.message);
            setLoadingUpdate(false);
            return;
        }

        Toast.Show("success", response.message);
        setLoadingUpdate(false);
        navigate(-1);
    };

    return (
        <Layout>
            <div className="pageContainer">
                <div className="pageHeader">
                    <PageTitle title={`${intl.formatMessage({ id: "Screens.CounterTypes.Edit.Title" })} ${counterType.code || ''}`} allowGoBack={true} />
                </div>
                <div className="pageBody">
                    <label style={{ margin: "0px", padding: "13px" }}>
                        <b>{intl.formatMessage({ id: "Screens.CounterTypes.Edit.SubTitle" })}</b>
                    </label>
                    <Row
                        style={{
                            margin: "0px",
                            padding: "0px",
                            alignItems: "flex-end",
                            alignContent: "flex-end",
                        }}
                    >
                        <Row>
                            <InputComponent
                                xl={4}
                                lg={6}
                                md={12}
                                sm={12}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                value={counterType.code || ''}
                                label={intl.formatMessage({ id: "Screens.CounterTypes.Table.Code" })}
                                readOnly={true}
                            />
                        </Row>
                        <Row>
                            <InputComponent
                                xl={4}
                                lg={6}
                                md={12}
                                sm={12}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                value={counterType.name || ''}
                                label={intl.formatMessage({ id: "Screens.CounterTypes.Table.Name" })}
                                readOnly={isErpCountersActive}
                                onChange={(t: string) => {
                                    setCounterType((prevState: CounterTypeDTO) => ({ ...prevState, name: t }))
                                }}
                                required={true}
                            />
                        </Row>
                        <Row>
                            <InputComponent
                                xl={4}
                                lg={6}
                                md={12}
                                sm={12}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                value={counterType.unit || ''}
                                label={intl.formatMessage({ id: "Screens.CounterTypes.Table.Unit" })}
                                readOnly={true}
                            />
                        </Row>
                        {isErpCountersActive && (
                            <Row>
                                <InputComponent
                                    xl={4}
                                    lg={6}
                                    md={12}
                                    sm={12}
                                    xs={12}
                                    style={{ width: "100%" }}
                                    type={"number"}
                                    value={counterType.order?.toString() || ''}
                                    label={intl.formatMessage({ id: "Screens.CounterTypes.Table.Order" })}
                                    readOnly={true}
                                />
                            </Row>
                        )}
                        <Row>
                            <InputComponent
                                xl={4}
                                lg={6}
                                md={12}
                                sm={12}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"textArea"}
                                value={counterType.description || ''}
                                label={intl.formatMessage({ id: "Screens.CounterTypes.Table.Description" })}
                                readOnly={isErpCountersActive}
                                onChange={(t: string) => {
                                    setCounterType((prevState: CounterTypeDTO) => ({ ...prevState, description: t }))
                                }}
                            />
                        </Row>
                        <Row>
                            <Col xl={4} lg={6} md={12} sm={12} xs={12}>
                                <div className="form-check" style={{ paddingTop: "10px" }}>
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={counterType.active}
                                        disabled={isErpCountersActive}
                                        onChange={(e) => {
                                            setCounterType((prevState: CounterTypeDTO) => ({ ...prevState, active: e.target.checked }))
                                        }}
                                    />
                                    <label className="form-check-label">
                                        {intl.formatMessage({ id: "Screens.CounterTypes.Table.Active" })}
                                    </label>
                                </div>
                            </Col>
                        </Row>
                        {isErpCountersActive && (
                            <Row>
                                <Col xl={4} lg={6} md={12} sm={12} xs={12}>
                                    <div className="form-check" style={{ paddingTop: "10px" }}>
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={counterType.syncWithErp}
                                            disabled={true}
                                            readOnly
                                        />
                                        <label className="form-check-label">
                                            {intl.formatMessage({ id: "Screens.CounterTypes.Table.SyncWithErp" })}
                                        </label>
                                    </div>
                                </Col>
                            </Row>
                        )}
                    </Row>
                    <Row style={{ margin: "0px", marginTop: "10px", padding: "0px", display: "flex", gap: "10px" }}>
                        <Col xl={1} lg={1} md={1} sm={1} xs={1}>
                            <ButtonComponent
                                disabled={loadingUpdate || isErpCountersActive}
                                loading={loadingUpdate}
                                text={intl.formatMessage({ id: "Common.Buttons.Save" })}
                                onClick={editCounterType}
                            />
                        </Col>
                    </Row>
                </div>
            </div>
        </Layout>
    );
}
